import { useCreateStreamMutation } from 'common/api/proctor';
import { StreamingProvider } from 'globals/enums';
import { handleError } from 'helpers/sentry';
import getRoom from 'helpers/stream';
import { RemoteParticipant } from 'livekit-client';
import { useEffect, useState, useRef } from 'react';
import Video, { RemoteTrack } from 'twilio-video';

export interface ParticipantData {
  session_id: string;
  primary: {
    tracks: RemoteTrack[];
  };
  secondary: {
    tracks: RemoteTrack[];
  };
}

export const parseParticipantIdentity = (identity: string) => {
  const parts = identity?.split('_') ?? [];
  if (parts.length < 3) {
    const [name = '', session_id = '', type = ''] = parts;
    return { name, session_id, type };
  }
  const type = parts.pop();
  const session_id = parts.pop();
  const name = parts.join('_');
  return { name, session_id, type };
};

const getTrackKey = (track: any) => {
  // LiveKit exposes `sid` while Twilio falls back to `trackSid`/`name`.
  return track?.['sid'] || track?.['trackSid'] || track?.name;
};

const useLiveStream = (participant_group_id, handleReconnectingStatus) => {
  const [createStream] = useCreateStreamMutation();
  const participantsRef = useRef<Map<string, ParticipantData>>(new Map());
  const [participants, setParticipants] = useState<ParticipantData[]>([]);

  const emitParticipants = () => {
    setParticipants(Array.from(participantsRef.current.values()));
  };

  const upsertParticipant = (session_id: string, updater: (current?: ParticipantData) => ParticipantData | undefined) => {
    const current = participantsRef.current.get(session_id);
    const next = updater(current);
    if (next) {
      participantsRef.current.set(session_id, next);
      if (next !== current) {
        emitParticipants();
      }
    } else if (participantsRef.current.has(session_id)) {
      participantsRef.current.delete(session_id);
      emitParticipants();
    }
  };

  const addParticipant = (participant: any, track: Video.RemoteTrack) => {
    const { name, session_id, type } = parseParticipantIdentity(participant.identity);
    if (name === 'host' || !session_id) return;
    const trackKey = getTrackKey(track);
    upsertParticipant(session_id, (existing) => {
      const baseParticipant: ParticipantData =
        existing || {
          session_id,
          primary: { tracks: [] },
          secondary: { tracks: [] },
        };
      const targetTracks =
        type === 'secondary' ? baseParticipant.secondary.tracks : baseParticipant.primary.tracks;
      const hasTrack = targetTracks.some((item) => getTrackKey(item) === trackKey);
      if (hasTrack) {
        return existing;
      }
      const nextParticipant: ParticipantData = {
        session_id,
        primary:
          type === 'primary'
            ? { tracks: [...baseParticipant.primary.tracks, track] }
            : { tracks: [...baseParticipant.primary.tracks] },
        secondary:
          type === 'secondary'
            ? { tracks: [...baseParticipant.secondary.tracks, track] }
            : { tracks: [...baseParticipant.secondary.tracks] },
      };
      return nextParticipant;
    });
  };

  const updateParticipant = (
    participant: RemoteParticipant | Video.RemoteParticipant,
    track: any,
  ) => {
    const { session_id, type } = parseParticipantIdentity(participant.identity);
    if (!session_id) return;
    const trackKey = getTrackKey(track);
    upsertParticipant(session_id, (existing) => {
      if (!existing) return existing;
      const nextPrimaryTracks =
        type === 'primary'
          ? existing.primary.tracks.filter((item) =>
              trackKey ? getTrackKey(item) !== trackKey : item?.kind !== track.kind,
            )
          : existing.primary.tracks;
      const nextSecondaryTracks =
        type === 'secondary'
          ? existing.secondary.tracks.filter((item) =>
              trackKey ? getTrackKey(item) !== trackKey : item?.kind !== track.kind,
            )
          : existing.secondary.tracks;
      if (
        nextPrimaryTracks === existing.primary.tracks &&
        nextSecondaryTracks === existing.secondary.tracks
      ) {
        return existing;
      }
      return {
        session_id,
        primary: { tracks: nextPrimaryTracks },
        secondary: { tracks: nextSecondaryTracks },
      };
    });
  };

  useEffect(() => {
    if (participant_group_id) {
      participantsRef.current.clear();
      setParticipants([]);
      const initializeLiveStream = async () => {
        try {
          const streamObject = await createStream({
            participant_group_id,
          }).unwrap();

          const room: any = await getRoom(streamObject);

          room.on('trackSubscribed', (track, publication, participant) => {
            addParticipant(participant, track);
          });

          room.on('trackUnsubscribed', (track, publication, participant) => {
            updateParticipant(participant, track);
          });

          room.on('participantReconnecting', (participant) => {
            handleReconnectingStatus(participant, true);
          });

          room.on('participantReconnected', (participant) => {
            handleReconnectingStatus(participant, false);
          });

          room.on('participantDisconnected', (participant) => {
            handleReconnectingStatus(participant, false);
          });

          if (streamObject?.video_provider === StreamingProvider.LiveKit) {
            room.remoteParticipants.forEach((participant) => {
              participant.trackPublications?.forEach((publication) => {
                if (publication.isSubscribed) {
                  const track = publication.track;
                  addParticipant(participant, track);
                }
              });
            });
          } else {
            room.participants.forEach((participant) => {
              participant.tracks?.forEach((publication) => {
                if (publication.isSubscribed) {
                  const track = publication.track;
                  addParticipant(participant, track);
                }
              });
            });
          }
        } catch (error) {
          handleError(error);
        }
      };

      initializeLiveStream();
    }
  }, [participant_group_id]);
  return participants;
};

export default useLiveStream;
